import { XYCoord } from 'dnd-core';
import React, { useRef } from 'react';
import { DragSourceMonitor, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';

import { OrderedEditorItem } from '../../widgets/ordered-editor-item/ordered-editor-item';

// Adapted from the following example:
// https://codesandbox.io/s/github/react-dnd/react-dnd/tree/gh-pages/examples_hooks_ts/04-sortable/simple?from-embed

export const ItemTypes = {
  DraggableEditorItem: 'draggable-editor-item'
};

const styles = require('./draggable-editor-item.css');

export interface DraggableEditorItemProps {
  id: number;
  element: JSX.Element | string;
  control?: JSX.Element;
  index: number;
  moveItem(dragIndex: number, hoverIndex: number): void;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const DraggableEditorItem: React.FC<DraggableEditorItemProps> = ({
  id,
  element,
  control,
  index,
  moveItem: moveItem
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop({
    accept: ItemTypes.DraggableEditorItem,
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveItem(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag] = useDrag({
    item: { type: ItemTypes.DraggableEditorItem, id, index },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));
  return (
    <div ref={ref} className={styles.draggableItem} style={{ opacity }}>
      <OrderedEditorItem element={element} control={control} isDragging={false} outlined={true} />
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/admin/ui/components/drag-and-drop/draggable-editor-item.tsx